// REQ-P1-06b: タスク紐づきイベント一覧の契約型付きQuery/API。

import { useQuery } from '@tanstack/react-query';
import { api, requestApiJson, type paths } from './client';
import type { components } from './types.gen';
import { ApiStatusError } from './templates';

type TaskProjectsQuery = NonNullable<paths['/admin/task-projects/mine']['get']['parameters']['query']>;
type TaskProjectsResponse = NonNullable<
  paths['/admin/task-projects/mine']['get']['responses'][200]['content']
>['application/json'];
type CastingSummaryResponse = NonNullable<
  paths['/common/events/casting-summary']['post']['responses'][200]['content']
>['application/json'];
type TicketSummaryResponse = NonNullable<
  paths['/events/{eventUuid}/ticket-sales/summary']['get']['responses'][200]['content']
>['application/json'];
type TicketMarketingTimelineResponse = NonNullable<
  paths['/events/{eventUuid}/ticket-sales/marketing-timeline']['get']['responses'][200]['content']
>['application/json'];
type TicketSalesListResponse = NonNullable<
  paths['/events/{eventUuid}/ticket-sales']['get']['responses'][200]['content']
>['application/json'];
type TicketSalesOverviewQuery = NonNullable<
  paths['/events/ticket-sales/overview']['get']['parameters']['query']
>;
type TicketSalesOverviewResponse = NonNullable<
  paths['/events/ticket-sales/overview']['get']['responses'][200]['content']
>['application/json'];
type TicketSalesMutationRequest = NonNullable<
  paths['/events/{eventUuid}/ticket-sales']['post']['requestBody']
>['content']['application/json'];
type TicketSalesMutationResponse = NonNullable<
  paths['/events/{eventUuid}/ticket-sales']['post']['responses'][200]['content']
>['application/json'];
export type TicketSalesShareSettings = components['schemas']['TicketSalesShareSettings'];
export type TicketSalesShareSettingsUpdateRequest = components['schemas']['TicketSalesShareSettingsUpdateRequest'];
export type ArtistSharedTicketSales = components['schemas']['ArtistSharedTicketSales'];
export type EventGuestTicketAdminDashboard = components['schemas']['AdminDashboard'];
export type EventGuestTicketArtistDashboard = components['schemas']['ArtistDashboard'];
export type EventGuestTicketRegistration = components['schemas']['Registration'];
export type EventGuestTicketAllocation = components['schemas']['Allocation'];
export type EventGuestTicketSettings = components['schemas']['GuestTicketSettings'];
export type EventGuestTicketSettingsUpdateRequest = components['schemas']['SettingsUpdateRequest'];
export type EventGuestTicketAllocationRequest = components['schemas']['AllocationMutationRequest'];
export type EventGuestTicketAllocateAllRequest = components['schemas']['AllocateAllRequest'];
export type EventGuestTicketAdminRegistrationCreateRequest = components['schemas']['AdminRegistrationCreateRequest'];
export type EventGuestTicketArtistRegistrationCreateRequest = components['schemas']['ArtistRegistrationCreateRequest'];
export type EventGuestTicketAdminRegistrationUpdateRequest = components['schemas']['AdminRegistrationUpdateRequest'];
export type EventGuestTicketArtistRegistrationUpdateRequest = components['schemas']['ArtistRegistrationUpdateRequest'];
export type EventGuestTicketStatusRequest = components['schemas']['RegistrationStatusRequest'];
type EventTaskSummariesRequest = NonNullable<
  paths['/common/events/task-summary']['post']['requestBody']
>['content']['application/json'];
type EventTaskSummariesResponse = NonNullable<
  paths['/common/events/task-summary']['post']['responses'][200]['content']
>['application/json'];
type TimetableListResponse = NonNullable<
  paths['/events/{eventUuid}/timetables']['get']['responses'][200]['content']
>['application/json'];
type TimetableMutationRequest = NonNullable<
  paths['/events/{eventUuid}/timetables']['post']['requestBody']
>['content']['application/json'];
type AdminEventsQuery = NonNullable<
  paths['/events/filter-by-date']['get']['parameters']['query']
>;
type AdminEventsResponse = NonNullable<
  paths['/events/filter-by-date']['get']['responses'][200]['content']
>['application/json'];
type ArtistEventsQuery = NonNullable<
  paths['/artist/events/filter-by-date']['get']['parameters']['query']
>;
type ArtistEventsResponse = NonNullable<
  paths['/artist/events/filter-by-date']['get']['responses'][200]['content']
>['application/json'];
type AdminEventCalendarSummaryQuery = NonNullable<
  paths['/events/calendar-summary']['get']['parameters']['query']
>;
type AdminEventCalendarSummaryResponse = NonNullable<
  paths['/events/calendar-summary']['get']['responses'][200]['content']
>['application/json'];
type ArtistEventCalendarSummaryQuery = NonNullable<
  paths['/artist/events/calendar-summary']['get']['parameters']['query']
>;
type ArtistEventCalendarSummaryResponse = NonNullable<
  paths['/artist/events/calendar-summary']['get']['responses'][200]['content']
>['application/json'];
type TimetableRuntimeResponse = NonNullable<
  paths['/events/{eventUuid}/timetables/{timetableId}/runtime']['get']['responses'][200]['content']
>['application/json'];
type SharedTimetableListResponse = NonNullable<
  paths['/events/{eventUuid}/timetables/shared']['get']['responses'][200]['content']
>['application/json'];
type TimetableAdjustmentRequest = NonNullable<
  paths['/events/{eventUuid}/timetables/{timetableId}/adjustments']['post']['requestBody']
>['content']['application/json'];
type TimetableMemoRequest = NonNullable<
  paths['/events/{eventUuid}/timetables/{timetableId}/memos']['post']['requestBody']
>['content']['application/json'];

const TICKET_SUMMARY_RETRY_DELAYS_MS = [350, 900];
const TRANSIENT_GATEWAY_STATUSES = new Set([502, 503, 504]);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchTicketSalesSummaryWithRetry(eventUuid: string) {
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= TICKET_SUMMARY_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const result = await api.GET('/events/{eventUuid}/ticket-sales/summary', {
        params: { path: { eventUuid } },
      });
      if (
        !TRANSIENT_GATEWAY_STATUSES.has(result.response.status) ||
        attempt >= TICKET_SUMMARY_RETRY_DELAYS_MS.length
      ) {
        return result;
      }
    } catch (error) {
      lastError = error;
      if (attempt >= TICKET_SUMMARY_RETRY_DELAYS_MS.length) throw error;
    }
    await sleep(TICKET_SUMMARY_RETRY_DELAYS_MS[attempt] || 0);
  }
  throw lastError || new Error('券売サマリーの取得に失敗しました');
}
type TimetableAnnouncementRequest = NonNullable<
  paths['/events/{eventUuid}/timetable-announcements']['post']['requestBody']
>['content']['application/json'];
type StagePublicLinkRequest = NonNullable<
  paths['/admin/events/{eventId}/stage-public-links']['post']['requestBody']
>['content']['application/json'];
type StageSeatingLayoutRequest = components['requestBodies']['StageSeatingLayoutPayload']['content']['application/json'];
type StageSeatingTextProjectionRequest = NonNullable<
  paths['/admin/events/{eventId}/stage-seating/text-projection']['post']['requestBody']
>['content']['application/json'];
type StageSeatingNoteRequest = components['requestBodies']['StageSeatingNotePayload']['content']['application/json'];
type StageSeatingViewer = 'admin' | 'artist';
type StageSeatingModeQuery = NonNullable<
  paths['/admin/events/{eventId}/stage-seating']['get']['parameters']['query']
>;
type DeleteStageSeatingLayoutsRequest = NonNullable<
  paths['/admin/events/{eventId}/stage-seating']['delete']['requestBody']
>['content']['application/json'];
type ReorderStageSeatingLayoutsRequest = NonNullable<
  paths['/admin/events/{eventId}/stage-seating/order']['patch']['requestBody']
>['content']['application/json'];
type CopyStageSeatingLayoutRequest = NonNullable<
  paths['/admin/events/{eventId}/stage-seating/copy']['post']['requestBody']
>['content']['application/json'];
type StageSeatingShareSettingsRequest = NonNullable<
  paths['/admin/events/{eventId}/stage-seating/share']['put']['requestBody']
>['content']['application/json'];
type ActiveArtistTimetablesQuery = NonNullable<
  paths['/artist/timetables/active']['get']['parameters']['query']
>;
type ActiveArtistTimetablesResponse = NonNullable<
  paths['/artist/timetables/active']['get']['responses'][200]['content']
>['application/json'];
type ActiveAdminTimetablesQuery = NonNullable<
  paths['/admin/timetables/active']['get']['parameters']['query']
>;
type ActiveAdminTimetablesResponse = NonNullable<
  paths['/admin/timetables/active']['get']['responses'][200]['content']
>['application/json'];
type EventAssignmentAdminCandidatesResponse = NonNullable<
  paths['/admin/events/assignment-admin-candidates']['get']['responses'][200]['content']
>['application/json'];
type EventAdminAssignmentsResponse = NonNullable<
  paths['/events/{eventUuid}/assignments']['get']['responses'][200]['content']
>['application/json'];
type ReplaceEventAdminAssignmentsRequest = NonNullable<
  paths['/events/{eventUuid}/assignments']['put']['requestBody']
>['content']['application/json'];
type SelfAssignEventsRequest = NonNullable<
  paths['/events/assignments/self']['post']['requestBody']
>['content']['application/json'];
type SelfAssignEventsResponse = NonNullable<
  paths['/events/assignments/self']['post']['responses'][200]['content']
>['application/json'];
type EventMembersVisibilityRequest = NonNullable<
  paths['/events/{eventUuid}/members-visibility']['post']['requestBody']
>['content']['application/json'];
type EventFlyerUpdateRequest = NonNullable<
  paths['/events/{eventUuid}/update-flyer']['post']['requestBody']
>['content']['multipart/form-data'];
type EventFlyerDeleteRequest = NonNullable<
  paths['/events/{eventUuid}/delete-flyer']['post']['requestBody']
>['content']['application/json'];
type EventSearchQuery = NonNullable<
  paths['/events/search']['get']['parameters']['query']
>;
type EventSearchResponse = NonNullable<
  paths['/events/search']['get']['responses'][200]['content']
>['application/json'];
type EventsByUuidsQuery = NonNullable<
  paths['/events/by-uuids']['get']['parameters']['query']
>;
type EventsByUuidsResponse = NonNullable<
  paths['/events/by-uuids']['get']['responses'][200]['content']
>['application/json'];
type EventParentCandidatesQuery = NonNullable<
  paths['/events/{eventUuid}/parent-candidates']['get']['parameters']['query']
>;
type EventParentCandidatesResponse = NonNullable<
  paths['/events/{eventUuid}/parent-candidates']['get']['responses'][200]['content']
>['application/json'];
type EventImageCandidatesQuery = NonNullable<
  paths['/events/image-candidates']['get']['parameters']['query']
>;
type EventImageCandidatesResponse = NonNullable<
  paths['/events/image-candidates']['get']['responses'][200]['content']
>['application/json'];
type FirstUpcomingEventsResponse = NonNullable<
  paths['/events/first-upcoming']['get']['responses'][200]['content']
>['application/json'];
type CreateEventRequest = NonNullable<
  paths['/events']['post']['requestBody']
>['content']['multipart/form-data'];
type CreateEventResponse = NonNullable<
  paths['/events']['post']['responses'][201]['content']
>['application/json'];
type UpdateEventRequest = NonNullable<
  paths['/events/{eventUuid}']['put']['requestBody']
>['content']['multipart/form-data'];
type UpdateEventResponse = NonNullable<
  paths['/events/{eventUuid}']['put']['responses'][200]['content']
>['application/json'];
type DeleteEventResponse = NonNullable<
  paths['/events/{eventUuid}']['delete']['responses'][200]['content']
>['application/json'];
type EventImageReferenceRequest = NonNullable<
  paths['/events/{eventUuid}/image-reference']['post']['requestBody']
>['content']['application/json'];
type EventDeletionCheckResponse = NonNullable<
  paths['/events/{eventUuid}/deletion-check']['get']['responses'][200]['content']
>['application/json'];

export type TaskLinkedEvent = {
  eventUuid: string;
  eventName: string;
  eventDate: string | null;
  projectCount: number;
  totalTaskCount: number;
  doneTaskCount: number;
  openTaskCount: number;
  projectNames: string[];
  detail: Record<string, unknown> | null;
  taskCompletionRate: number;
};

type ApiJsonResult = {
  data?: string | unknown;
  error?: string | unknown;
  response: Response;
};

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed.message === 'string') return parsed.message;
      if (parsed && typeof parsed.error === 'string') return parsed.error;
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiStatusError(status: number, error?: unknown, fallbackMessage?: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage || statusError.message;
  throw statusError;
}

function parseJsonText(raw: unknown, fallbackMessage: string): Record<string, unknown> {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
  const text = typeof raw === 'string' ? raw : '';
  try {
    const parsed = text ? JSON.parse(text) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    throw new Error(fallbackMessage);
  }
}

function parseApiJsonResult(
  result: ApiJsonResult,
  fallbackMessage: string
): Record<string, unknown> {
  if (!result.response.ok) {
    throwApiStatusError(result.response.status, result.error, fallbackMessage);
  }
  return parseJsonText(result.data, fallbackMessage);
}

async function parseResponseJson(response: Response): Promise<Record<string, unknown>> {
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

async function throwApiStatusErrorWithPayload(
  response: Response,
  error: unknown,
  fallbackMessage: string
): Promise<never> {
  const payload = await parseResponseJson(response);
  const message =
    (typeof payload.message === 'string' && payload.message) ||
    getApiErrorMessage(error) ||
    fallbackMessage;
  const statusError = new ApiStatusError(response.status) as ApiStatusError & {
    data?: Record<string, unknown>;
  };
  statusError.message = message;
  statusError.data = payload;
  throw statusError;
}

function requireSuccess(payload: Record<string, unknown>, fallbackMessage: string): void {
  if (payload.success !== true) {
    const message = typeof payload.message === 'string' ? payload.message : fallbackMessage;
    throw new Error(message);
  }
}

export async function listMyTaskProjectsPage(query: TaskProjectsQuery): Promise<TaskProjectsResponse> {
  const { data, response } = await api.GET('/admin/task-projects/mine', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data;
}

export async function listAllMyTaskProjects(): Promise<Record<string, unknown>[]> {
  const limit = 100;
  let offset = 0;
  let total: number | null = null;
  const allProjects: Record<string, unknown>[] = [];

  while (total == null || allProjects.length < total) {
    const page = await listMyTaskProjectsPage({ limit, offset });
    const pageItems = Array.isArray(page.items) ? page.items : [];
    allProjects.push(...pageItems);
    const nextTotal = Number(page.total);
    total = Number.isFinite(nextTotal) ? nextTotal : allProjects.length;
    if (pageItems.length < limit) break;
    offset += limit;
  }

  return allProjects;
}

export async function getEventDetail(eventUuid: string): Promise<Record<string, unknown> | null> {
  const { data, response } = await api.GET('/events/{eventUuid}', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) return null;
  const event = (data as { event?: unknown }).event;
  return event && typeof event === 'object' ? (event as Record<string, unknown>) : null;
}

export async function getEventDetailOrThrow(eventUuid: string): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.GET('/events/{eventUuid}', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'イベント情報の取得に失敗しました');
  }
  const event = (data as { event?: unknown }).event;
  if (!event || typeof event !== 'object') throw new Error('イベント情報が見つかりません');
  return event as Record<string, unknown>;
}

export interface ArtistContractScheduleEntry {
  schedule_date: string;
  note?: string;
  start_time?: string;
  end_time?: string;
  place_name?: string;
  place_address?: string;
  event_uuid?: string | null;
  event_name?: string | null;
  venue?: string;
}

// フェーズ6: アーティスト専用カレンダー用。成立済み契約に紐づくスケジュールを日付範囲で取得。
export async function listArtistContractSchedule(
  query: { startDate?: string; endDate?: string } = {}
): Promise<ArtistContractScheduleEntry[]> {
  const { data, response } = await requestApiJson<{ schedules?: ArtistContractScheduleEntry[] }>(
    '/artist/contract-schedule/by-date',
    { method: 'GET', query: query as Record<string, unknown> }
  );
  if (!response.ok || !data) return [];
  return Array.isArray(data.schedules) ? data.schedules : [];
}

export interface AppendEventAdditionalDateInput {
  additional_date: string;
  additional_date_title?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
}

// フェーズ3: 契約スケジュールの日付をイベントの追加日程へ逆追加する（生成型外のため requestApiJson 経由）。
export async function appendEventAdditionalDates(
  eventUuid: string,
  dates: AppendEventAdditionalDateInput[]
): Promise<{ inserted_count: number; skipped_count: number } & Record<string, unknown>> {
  const { data, error, response } = await requestApiJson<
    { inserted_count: number; skipped_count: number } & Record<string, unknown>
  >(`/admin/events/${encodeURIComponent(eventUuid)}/additional-dates`, {
    method: 'POST',
    body: { dates },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '追加日程の反映に失敗しました');
  }
  return data;
}

export async function searchEvents(query: EventSearchQuery): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/events/search', {
    params: { query },
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, 'イベント検索に失敗しました');
  const events = (data as EventSearchResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function getEventsByUuids(query: EventsByUuidsQuery): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/events/by-uuids', {
    params: { query },
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, 'イベント情報の取得に失敗しました');
  const events = (data as EventsByUuidsResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function listEventParentCandidates(
  eventUuid: string,
  query: EventParentCandidatesQuery = {}
): Promise<EventParentCandidatesResponse> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/parent-candidates', {
    params: { path: { eventUuid }, query },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '親公演候補の取得に失敗しました');
  }
  return data as EventParentCandidatesResponse;
}

export async function listEventImageCandidates(
  query: EventImageCandidatesQuery
): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/events/image-candidates', {
    params: { query },
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, '画像候補の取得に失敗しました');
  const payload = data as EventImageCandidatesResponse & {
    previous?: Record<string, unknown>[];
    next?: Record<string, unknown>[];
  };
  if (Array.isArray(payload.events)) return payload.events;
  return [...(payload.previous || []), ...(payload.next || [])];
}

export async function listFirstUpcomingEvents(): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/events/first-upcoming');
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '既存イベントの取得に失敗しました');
  }
  const events = (data as FirstUpcomingEventsResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function createAdminEvent(body: FormData): Promise<CreateEventResponse> {
  const { data, error, response } = await api.POST('/events', {
    body: body as unknown as CreateEventRequest,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'イベントの登録に失敗しました');
  }
  return data as CreateEventResponse;
}

export async function updateAdminEvent(
  eventUuid: string,
  body: FormData
): Promise<UpdateEventResponse> {
  const { data, error, response } = await api.PUT('/events/{eventUuid}', {
    params: { path: { eventUuid } },
    body: body as unknown as UpdateEventRequest,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'イベント情報の更新に失敗しました');
  }
  return data as UpdateEventResponse;
}

export async function deleteAdminEvent(eventUuid: string): Promise<DeleteEventResponse> {
  const { data, error, response } = await api.DELETE('/events/{eventUuid}', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) {
    await throwApiStatusErrorWithPayload(response, error, 'イベントの削除に失敗しました');
  }
  return data as DeleteEventResponse;
}

export async function setEventImageReference(
  eventUuid: string,
  body: EventImageReferenceRequest
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/image-reference', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, '画像の参照に失敗しました');
  return data as Record<string, unknown>;
}

export async function checkEventDeletion(eventUuid: string): Promise<EventDeletionCheckResponse> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/deletion-check', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, '削除可否の確認に失敗しました');
  return data as EventDeletionCheckResponse;
}

export async function updateEventMembersVisibility(
  eventUuid: string,
  body: EventMembersVisibilityRequest
): Promise<Record<string, unknown>> {
  const { error, response } = await api.POST('/events/{eventUuid}/members-visibility', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok) {
    await throwApiStatusErrorWithPayload(response, error, '参加アーティストの公開設定に失敗しました');
  }
  return parseResponseJson(response);
}

export async function updateEventFlyer(
  eventUuid: string,
  body: EventFlyerUpdateRequest
): Promise<string> {
  const result = await api.POST('/events/{eventUuid}/update-flyer', {
    params: { path: { eventUuid } },
    body,
    parseAs: 'text',
  });
  const payload = parseApiJsonResult(result, 'フライヤーの更新に失敗しました');
  requireSuccess(payload, 'フライヤーの更新に失敗しました');
  const flyerUrl = typeof payload.flyerUrl === 'string' ? payload.flyerUrl : '';
  if (!flyerUrl) throw new Error('アップロード結果のファイル名を取得できませんでした');
  return flyerUrl;
}

export async function deleteEventFlyer(
  eventUuid: string,
  body: EventFlyerDeleteRequest
): Promise<void> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/delete-flyer', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'フライヤーの削除に失敗しました');
  }
  requireSuccess(data as Record<string, unknown>, 'フライヤーの削除に失敗しました');
}

export async function getCastingSummaries(eventUuids: string[]) {
  const { data, response } = await api.POST('/common/events/casting-summary', {
    body: { eventUuids },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return (data as CastingSummaryResponse).summaries || {};
}

export async function getEventTicketSalesSummary(eventUuid: string) {
  const { data, response } = await fetchTicketSalesSummaryWithRetry(eventUuid);
  if (!response.ok || !data) return null;
  return (data as TicketSummaryResponse).summary || null;
}

export async function getEventTicketSalesSummaryOrThrow(eventUuid: string) {
  const { data, error, response } = await fetchTicketSalesSummaryWithRetry(eventUuid);
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '券売サマリーの取得に失敗しました');
  }
  return (data as TicketSummaryResponse).summary || null;
}

export async function getEventTicketSalesMarketingTimeline(eventUuid: string) {
  const { data, error, response } = await api.GET('/events/{eventUuid}/ticket-sales/marketing-timeline', {
    params: { path: { eventUuid } },
    cache: 'no-store',
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '券売と施策の推移取得に失敗しました');
  }
  return (data as TicketMarketingTimelineResponse).timeline;
}

export async function listEventTicketSales(eventUuid: string): Promise<TicketSalesListResponse> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/ticket-sales', {
    params: { path: { eventUuid } },
    cache: 'no-store',
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '券売状況の取得に失敗しました');
  }
  return data as TicketSalesListResponse;
}

export async function controlAutomaticTicketSalesEntry(
  eventUuid: string,
  entryId: number,
  action: 'convert_to_manual' | 'exclude'
): Promise<{
  entryId: number;
  action: 'convert_to_manual' | 'exclude';
  saleDate: string;
  soldCount: number;
  revenue: number;
}> {
  const { data, error, response } = await requestApiJson<{
    success: true;
    result: {
      entryId: number;
      action: 'convert_to_manual' | 'exclude';
      saleDate: string;
      soldCount: number;
      revenue: number;
    };
  }>(`/events/${encodeURIComponent(eventUuid)}/ticket-sales/automatic-entries/${entryId}/control`, {
    method: 'POST',
    body: { action },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '自動取得記録を変更できませんでした');
  }
  return data.result;
}

export async function getEventTicketSalesShareSettings(
  eventUuid: string
): Promise<TicketSalesShareSettings> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/ticket-sales/share-settings', {
    params: { path: { eventUuid } },
    cache: 'no-store',
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '券売状況の共有設定を取得できませんでした');
  }
  return data.settings;
}

export async function updateEventTicketSalesShareSettings(
  eventUuid: string,
  body: TicketSalesShareSettingsUpdateRequest
): Promise<TicketSalesShareSettings> {
  const { data, error, response } = await api.PUT('/events/{eventUuid}/ticket-sales/share-settings', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '券売状況の共有設定を保存できませんでした');
  }
  return data.settings;
}

export async function getArtistSharedTicketSales(
  eventUuid: string,
  month: string
): Promise<ArtistSharedTicketSales> {
  const { data, error, response } = await api.GET('/artist/events/{eventUuid}/ticket-sales/shared', {
    params: { path: { eventUuid }, query: { month } },
    cache: 'no-store',
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '共有された券売状況を取得できませんでした');
  }
  return data;
}

export async function getEventGuestTicketDashboard(
  eventUuid: string
): Promise<EventGuestTicketAdminDashboard> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/guest-tickets', {
    params: { path: { eventUuid } },
    cache: 'no-store',
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '取り置き・招待情報を取得できませんでした');
  }
  return data.dashboard;
}

export async function updateEventGuestTicketSettings(
  eventUuid: string,
  body: EventGuestTicketSettingsUpdateRequest
): Promise<EventGuestTicketSettings> {
  const { data, error, response } = await api.PUT('/events/{eventUuid}/guest-tickets/settings', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '入力項目と受付期限を保存できませんでした');
  }
  return data.settings;
}

export async function upsertEventGuestTicketAllocation(
  eventUuid: string,
  body: EventGuestTicketAllocationRequest
): Promise<EventGuestTicketAllocation> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/guest-tickets/allocations', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '参加者の取り置き・招待枠を保存できませんでした');
  }
  return data.allocation;
}

export async function allocateEventGuestTicketsToAllParticipants(
  eventUuid: string,
  body: EventGuestTicketAllocateAllRequest
): Promise<EventGuestTicketAllocation[]> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/guest-tickets/allocations/all-participants', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '参加者全員の取り置き・招待枠を保存できませんでした');
  }
  return data.allocations;
}

export async function createEventGuestTicketRegistration(
  eventUuid: string,
  body: EventGuestTicketAdminRegistrationCreateRequest
): Promise<EventGuestTicketRegistration> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/guest-tickets/registrations', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '取り置き・招待を登録できませんでした');
  }
  return data.registration;
}

export async function updateEventGuestTicketRegistration(
  eventUuid: string,
  registrationId: string,
  body: EventGuestTicketAdminRegistrationUpdateRequest
): Promise<EventGuestTicketRegistration> {
  const { data, error, response } = await api.PATCH('/events/{eventUuid}/guest-tickets/registrations/{registrationId}', {
    params: { path: { eventUuid, registrationId } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '取り置き・招待情報を更新できませんでした');
  }
  return data.registration;
}

export async function changeEventGuestTicketRegistrationStatus(
  eventUuid: string,
  registrationId: string,
  body: EventGuestTicketStatusRequest
): Promise<EventGuestTicketRegistration> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/guest-tickets/registrations/{registrationId}/status', {
    params: { path: { eventUuid, registrationId } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '取り置き・招待の状態を更新できませんでした');
  }
  return data.registration;
}

export async function publishEventGuestTicketAnnouncement(
  eventUuid: string,
  body: { deliveryScope: 'event_chat' | 'individual_chat'; artistIds: string[] }
): Promise<void> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/guest-tickets/announcements', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '登録案内を送信できませんでした');
  }
}

export async function getArtistEventGuestTicketDashboard(
  eventUuid: string
): Promise<EventGuestTicketArtistDashboard> {
  const { data, error, response } = await api.GET('/artist/events/{eventUuid}/guest-tickets', {
    params: { path: { eventUuid } },
    cache: 'no-store',
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '取り置き・招待枠を取得できませんでした');
  }
  return data.dashboard;
}

export async function createArtistEventGuestTicketRegistration(
  eventUuid: string,
  body: EventGuestTicketArtistRegistrationCreateRequest
): Promise<EventGuestTicketRegistration> {
  const { data, error, response } = await api.POST('/artist/events/{eventUuid}/guest-tickets/registrations', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'ゲストを登録できませんでした');
  }
  return data.registration;
}

export async function updateArtistEventGuestTicketRegistration(
  eventUuid: string,
  registrationId: string,
  body: EventGuestTicketArtistRegistrationUpdateRequest
): Promise<EventGuestTicketRegistration> {
  const { data, error, response } = await api.PATCH('/artist/events/{eventUuid}/guest-tickets/registrations/{registrationId}', {
    params: { path: { eventUuid, registrationId } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'ゲスト情報を更新できませんでした');
  }
  return data.registration;
}

export async function cancelArtistEventGuestTicketRegistration(
  eventUuid: string,
  registrationId: string
): Promise<EventGuestTicketRegistration> {
  const { data, error, response } = await api.POST('/artist/events/{eventUuid}/guest-tickets/registrations/{registrationId}/cancel', {
    params: { path: { eventUuid, registrationId } },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'ゲスト登録をキャンセルできませんでした');
  }
  return data.registration;
}

export async function listEventTicketSalesOverview(
  query: TicketSalesOverviewQuery = {}
): Promise<TicketSalesOverviewResponse> {
  const { data, error, response } = await api.GET('/events/ticket-sales/overview', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'チケット売上一覧の取得に失敗しました');
  }
  return data as TicketSalesOverviewResponse;
}

export async function createEventTicketSales(
  eventUuid: string,
  body: TicketSalesMutationRequest
): Promise<TicketSalesMutationResponse> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/ticket-sales', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '保存に失敗しました');
  }
  return data as TicketSalesMutationResponse;
}

export async function updateEventTicketSales(
  eventUuid: string,
  body: TicketSalesMutationRequest
): Promise<TicketSalesMutationResponse> {
  const { data, error, response } = await api.PUT('/events/{eventUuid}/ticket-sales', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '保存に失敗しました');
  }
  return data as TicketSalesMutationResponse;
}

export async function getEventTaskSummaries(body: EventTaskSummariesRequest) {
  const { data, response } = await api.POST('/common/events/task-summary', {
    body,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return (data as EventTaskSummariesResponse).summaries || {};
}

export async function listEventTimetables(eventUuid: string): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/timetables', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, 'タイムテーブルの取得に失敗しました');
  const timetables = (data as TimetableListResponse).timetables;
  return Array.isArray(timetables) ? timetables : [];
}

export async function createEventTimetable(
  eventUuid: string,
  body: TimetableMutationRequest
): Promise<Record<string, unknown> | null> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/timetables', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, 'タイムテーブルの作成に失敗しました');
  const mutation = data as { timetable?: unknown; merge?: unknown };
  const timetable = mutation.timetable;
  return timetable && typeof timetable === 'object'
    ? { ...(timetable as Record<string, unknown>), __merge: mutation.merge || null }
    : null;
}

export async function updateEventTimetable(
  eventUuid: string,
  timetableId: number | string,
  body: TimetableMutationRequest
): Promise<Record<string, unknown> | null> {
  const numericTimetableId = Number(timetableId);
  const { data, error, response } = await api.PUT('/events/{eventUuid}/timetables/{timetableId}', {
    params: { path: { eventUuid, timetableId: numericTimetableId } },
    body,
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, 'タイムテーブルの更新に失敗しました');
  const mutation = data as { timetable?: unknown; merge?: unknown };
  const timetable = mutation.timetable;
  return timetable && typeof timetable === 'object'
    ? { ...(timetable as Record<string, unknown>), __merge: mutation.merge || null }
    : null;
}

export async function listEventTimetableRevisions(
  eventUuid: string,
  timetableId: number | string
): Promise<Record<string, unknown>[]> {
  const result = await (api as any).GET('/events/{eventUuid}/timetables/{timetableId}/revisions', {
    params: { path: { eventUuid, timetableId: Number(timetableId) } },
    parseAs: 'text',
    cache: 'no-store',
  });
  const json = parseApiJsonResult(result, '版履歴の取得に失敗しました');
  return Array.isArray(json.revisions) ? json.revisions : [];
}

export async function createEventTimetableRevision(
  eventUuid: string,
  timetableId: number | string,
  label: string
): Promise<Record<string, unknown>> {
  const result = await (api as any).POST('/events/{eventUuid}/timetables/{timetableId}/revisions', {
    params: { path: { eventUuid, timetableId: Number(timetableId) } },
    body: { label },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '版の保存に失敗しました');
}

export async function restoreEventTimetableRevision(
  eventUuid: string,
  timetableId: number | string,
  revisionId: number | string,
  expectedUpdatedAt: string | null
): Promise<Record<string, unknown>> {
  const result = await (api as any).POST('/events/{eventUuid}/timetables/{timetableId}/revisions/{revisionId}/restore', {
    params: { path: { eventUuid, timetableId: Number(timetableId), revisionId: Number(revisionId) } },
    body: { expected_updated_at: expectedUpdatedAt },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '版の復元に失敗しました');
}

export async function notifyEventTimetableArtists(
  eventUuid: string,
  timetableId: number | string,
  body: Record<string, unknown> = {}
): Promise<Record<string, unknown>> {
  const numericTimetableId = Number(timetableId);
  const { data, error, response } = await (api as any).POST(
    '/events/{eventUuid}/timetables/{timetableId}/notify-artists',
    {
      params: { path: { eventUuid, timetableId: numericTimetableId } },
      body,
    }
  );
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, 'タイムテーブル通知に失敗しました');
  }
  return data as Record<string, unknown>;
}

export async function deleteEventTimetable(
  eventUuid: string,
  timetableId: number | string
): Promise<void> {
  const numericTimetableId = Number(timetableId);
  const { error, response } = await api.DELETE('/events/{eventUuid}/timetables/{timetableId}', {
    params: { path: { eventUuid, timetableId: numericTimetableId } },
  });
  if (!response.ok) throwApiStatusError(response.status, error, 'タイムテーブルの削除に失敗しました');
}

export async function listSharedEventTimetables(eventUuid: string): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/events/{eventUuid}/timetables/shared', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const timetables = (data as SharedTimetableListResponse).timetables;
  return Array.isArray(timetables) ? timetables : [];
}

export async function listArtistEventsByDate(
  query: ArtistEventsQuery = {}
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/artist/events/filter-by-date', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const events = (data as ArtistEventsResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function listAdminEventsByDate(
  query: AdminEventsQuery = {}
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/events/filter-by-date', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const events = (data as AdminEventsResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function listEventCalendarSummary(
  calendarType: 'admin' | 'artist',
  query: AdminEventCalendarSummaryQuery | ArtistEventCalendarSummaryQuery
): Promise<Record<string, unknown>[]> {
  const result =
    calendarType === 'artist'
      ? await api.GET('/artist/events/calendar-summary', {
          params: { query: query as ArtistEventCalendarSummaryQuery },
        })
      : await api.GET('/events/calendar-summary', {
          params: { query: query as AdminEventCalendarSummaryQuery },
        });
  if (!result.response.ok || !result.data) {
    throwApiStatusError(result.response.status, result.error, 'イベント概要の取得に失敗しました');
  }
  const events =
    calendarType === 'artist'
      ? (result.data as ArtistEventCalendarSummaryResponse).events
      : (result.data as AdminEventCalendarSummaryResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function getEventTimetableRuntime(
  eventUuid: string,
  timetableId: number | string
): Promise<{ adjustments: unknown[]; memos: unknown[]; announcements: unknown[]; permissions: unknown } | null> {
  try {
    return await getEventTimetableRuntimeState(eventUuid, timetableId);
  } catch {
    return null;
  }
}

export async function getEventTimetableRuntimeState(
  eventUuid: string,
  timetableId: number | string
): Promise<{ adjustments: unknown[]; memos: unknown[]; announcements: unknown[]; permissions: unknown }> {
  const numericTimetableId = Number(timetableId);
  if (!eventUuid || !Number.isFinite(numericTimetableId)) throw new ApiStatusError(400);

  const { data, error, response } = await api.GET('/events/{eventUuid}/timetables/{timetableId}/runtime', {
    params: { path: { eventUuid, timetableId: numericTimetableId } },
  });
  if (!response.ok || !data) throwApiStatusError(response.status, error, 'ランタイム情報の取得に失敗しました');
  const runtime = data as TimetableRuntimeResponse;
  return {
    adjustments: Array.isArray(runtime.adjustments) ? runtime.adjustments : [],
    memos: Array.isArray(runtime.memos) ? runtime.memos : [],
    announcements: Array.isArray(runtime.announcements) ? runtime.announcements : [],
    permissions: runtime.permissions || { can_edit: false },
  };
}

export async function upsertEventTimetableAdjustment(
  eventUuid: string,
  timetableId: number | string,
  body: TimetableAdjustmentRequest
) {
  const numericTimetableId = Number(timetableId);
  const { data, error, response } = await api.POST(
    '/events/{eventUuid}/timetables/{timetableId}/adjustments',
    {
      params: { path: { eventUuid, timetableId: numericTimetableId } },
      body,
    }
  );
  if (!response.ok) throwApiStatusError(response.status, error, '押し巻き設定の保存に失敗しました');
  return data || {};
}

export async function upsertEventTimetableMemo(
  eventUuid: string,
  timetableId: number | string,
  body: TimetableMemoRequest
) {
  const numericTimetableId = Number(timetableId);
  const { data, error, response } = await api.POST(
    '/events/{eventUuid}/timetables/{timetableId}/memos',
    {
      params: { path: { eventUuid, timetableId: numericTimetableId } },
      body,
    }
  );
  if (!response.ok) throwApiStatusError(response.status, error, 'メモの保存に失敗しました');
  return data || {};
}

export async function createEventTimetableAnnouncement(
  eventUuid: string,
  body: TimetableAnnouncementRequest
) {
  const { data, error, response } = await api.POST('/events/{eventUuid}/timetable-announcements', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok) throwApiStatusError(response.status, error, 'お知らせの追加に失敗しました');
  return data || {};
}

export async function getStageSeatingForViewer(
  eventUuid: string,
  mode: StageSeatingViewer,
  options: { mode?: string } = {}
) {
  const query: StageSeatingModeQuery | undefined = options.mode ? { mode: options.mode } : undefined;
  const result =
    mode === 'artist'
      ? await api.GET('/artist/events/{eventId}/stage-seating', {
          params: { path: { eventId: eventUuid }, query },
        })
      : await api.GET('/admin/events/{eventId}/stage-seating', {
          params: { path: { eventId: eventUuid }, query },
        });
  return parseApiJsonResult(result, 'シーティング情報の取得に失敗しました');
}

export async function saveStageSeatingLayout(
  eventUuid: string,
  viewer: StageSeatingViewer,
  body: StageSeatingLayoutRequest,
  idempotencyKey: string
) {
  const result =
    viewer === 'artist'
      ? await api.PUT('/artist/events/{eventId}/stage-seating', {
          params: { path: { eventId: eventUuid } },
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
          parseAs: 'text',
        })
      : await api.PUT('/admin/events/{eventId}/stage-seating', {
          params: { path: { eventId: eventUuid } },
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
          parseAs: 'text',
        });
  if (!result.response.ok) {
    await throwApiStatusErrorWithPayload(result.response, result.error, 'シーティングの保存に失敗しました');
  }
  return parseJsonText(result.data, 'シーティングの保存に失敗しました');
}

export async function createStageSeatingTextProjection(
  eventUuid: string,
  body: StageSeatingTextProjectionRequest
) {
  const result = await api.POST('/admin/events/{eventId}/stage-seating/text-projection', {
    params: { path: { eventId: eventUuid } },
    body,
  });
  return parseApiJsonResult(result, '席次テキストの作成に失敗しました');
}

export async function deleteStageSeatingLayouts(
  eventUuid: string,
  body: DeleteStageSeatingLayoutsRequest,
  idempotencyKey: string
) {
  const result = await api.DELETE('/admin/events/{eventId}/stage-seating', {
    params: { path: { eventId: eventUuid } },
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, 'シーティングの削除に失敗しました');
}

export async function createStageSeatingNote(
  eventUuid: string,
  viewer: StageSeatingViewer,
  body: StageSeatingNoteRequest
) {
  const result =
    viewer === 'artist'
      ? await api.POST('/artist/events/{eventId}/stage-seating/notes', {
          params: { path: { eventId: eventUuid } },
          body,
          parseAs: 'text',
        })
      : await api.POST('/admin/events/{eventId}/stage-seating/notes', {
          params: { path: { eventId: eventUuid } },
          body,
          parseAs: 'text',
        });
  return parseApiJsonResult(result, 'メモの保存に失敗しました');
}

export async function updateStageSeatingNote(
  eventUuid: string,
  viewer: StageSeatingViewer,
  noteId: number | string,
  body: StageSeatingNoteRequest
) {
  const result =
    viewer === 'artist'
      ? await api.PUT('/artist/events/{eventId}/stage-seating/notes/{noteId}', {
          params: { path: { eventId: eventUuid, noteId } },
          body,
          parseAs: 'text',
        })
      : await api.PUT('/admin/events/{eventId}/stage-seating/notes/{noteId}', {
          params: { path: { eventId: eventUuid, noteId } },
          body,
          parseAs: 'text',
        });
  return parseApiJsonResult(result, 'メモの更新に失敗しました');
}

export async function deleteStageSeatingNote(
  eventUuid: string,
  viewer: StageSeatingViewer,
  noteId: number | string
) {
  const result =
    viewer === 'artist'
      ? await api.DELETE('/artist/events/{eventId}/stage-seating/notes/{noteId}', {
          params: { path: { eventId: eventUuid, noteId } },
          parseAs: 'text',
        })
      : await api.DELETE('/admin/events/{eventId}/stage-seating/notes/{noteId}', {
          params: { path: { eventId: eventUuid, noteId } },
          parseAs: 'text',
        });
  return parseApiJsonResult(result, 'メモの削除に失敗しました');
}

export async function listStageSeatingNoteHistory(eventUuid: string, viewer: StageSeatingViewer) {
  const result =
    viewer === 'artist'
      ? await api.GET('/artist/events/{eventId}/stage-seating/notes/history', {
          params: { path: { eventId: eventUuid } },
          parseAs: 'text',
        })
      : await api.GET('/admin/events/{eventId}/stage-seating/notes/history', {
          params: { path: { eventId: eventUuid } },
          parseAs: 'text',
        });
  return parseApiJsonResult(result, 'メモ履歴の取得に失敗しました');
}

export async function applyParentStageSeatingLayouts(eventUuid: string, idempotencyKey: string) {
  const result = await api.POST('/admin/events/{eventId}/stage-seating/apply-parent', {
    params: { path: { eventId: eventUuid } },
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '親公演のシーティングコピーに失敗しました');
}

export async function copyStageSeatingLayout(
  eventUuid: string,
  body: CopyStageSeatingLayoutRequest,
  idempotencyKey: string
) {
  const result = await api.POST('/admin/events/{eventId}/stage-seating/copy', {
    params: { path: { eventId: eventUuid } },
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, 'シーティングの複製に失敗しました');
}

export async function reorderStageSeatingLayouts(
  eventUuid: string,
  body: ReorderStageSeatingLayoutsRequest,
  idempotencyKey: string
) {
  const result = await api.PATCH('/admin/events/{eventId}/stage-seating/order', {
    params: { path: { eventId: eventUuid } },
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '並び替えに失敗しました');
}

export async function saveStageSeatingShareSettings(
  eventUuid: string,
  body: StageSeatingShareSettingsRequest,
  idempotencyKey: string
) {
  const result = await api.PUT('/admin/events/{eventId}/stage-seating/share', {
    params: { path: { eventId: eventUuid } },
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '共有設定の保存に失敗しました');
}

export async function listStagePreviewPublicLinks(eventUuid: string) {
  const result = await api.GET('/admin/events/{eventId}/stage-public-links', {
    params: { path: { eventId: eventUuid } },
    parseAs: 'text',
    cache: 'no-store',
  });
  return parseApiJsonResult(result, '限定リンク一覧の取得に失敗しました');
}

export async function createStagePreviewPublicLink(
  eventUuid: string,
  body: StagePublicLinkRequest,
  idempotencyKey: string
) {
  const result = await api.POST('/admin/events/{eventId}/stage-public-links', {
    params: { path: { eventId: eventUuid } },
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '限定リンクの作成に失敗しました');
}

export async function revokeStagePreviewPublicLink(
  eventUuid: string,
  linkId: number,
  idempotencyKey: string
) {
  const result = await api.PATCH('/admin/events/{eventId}/stage-public-links/{linkId}/revoke', {
    params: { path: { eventId: eventUuid, linkId } },
    headers: { 'Idempotency-Key': idempotencyKey },
    parseAs: 'text',
  });
  return parseApiJsonResult(result, '限定リンクの失効に失敗しました');
}

export async function getPublicStagePreviewLink(token: string): Promise<Record<string, unknown>> {
  const result = await api.GET('/public/stage-links/{token}', {
    params: { path: { token } },
    parseAs: 'text',
    cache: 'no-store',
    credentials: 'omit',
  });
  return parseApiJsonResult(result, '限定プレビューを開けません');
}

export async function listActiveArtistTimetables(
  query: ActiveArtistTimetablesQuery
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/artist/timetables/active', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const timetables = (data as ActiveArtistTimetablesResponse).timetables;
  return Array.isArray(timetables) ? timetables : [];
}

export async function listActiveAdminTimetables(
  query: ActiveAdminTimetablesQuery
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/admin/timetables/active', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const timetables = (data as ActiveAdminTimetablesResponse).timetables;
  return Array.isArray(timetables) ? timetables : [];
}

export async function listEventAssignmentAdminCandidates(): Promise<EventAssignmentAdminCandidatesResponse> {
  const { data, error, response } = await api.GET('/admin/events/assignment-admin-candidates');
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '担当者候補の取得に失敗しました');
  }
  return data as EventAssignmentAdminCandidatesResponse;
}

export async function listEventAdminAssignments(
  eventUuid: string
): Promise<EventAdminAssignmentsResponse> {
  const { data, error, response } = await api.GET('/events/{eventUuid}/assignments', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '担当者情報の取得に失敗しました');
  }
  return data as EventAdminAssignmentsResponse;
}

export async function replaceEventAdminAssignments(
  eventUuid: string,
  body: ReplaceEventAdminAssignmentsRequest
): Promise<EventAdminAssignmentsResponse> {
  const { data, error, response } = await api.PUT('/events/{eventUuid}/assignments', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '担当者の更新に失敗しました');
  }
  return data as EventAdminAssignmentsResponse;
}

export async function selfAssignEvents(
  body: SelfAssignEventsRequest
): Promise<SelfAssignEventsResponse> {
  const { data, error, response } = await api.POST('/events/assignments/self', {
    body,
  });
  if (!response.ok || !data) {
    throwApiStatusError(response.status, error, '担当イベントの登録に失敗しました');
  }
  return data as SelfAssignEventsResponse;
}

export async function listTaskLinkedEvents(): Promise<TaskLinkedEvent[]> {
  const allProjects = await listAllMyTaskProjects();
  const aggregateMap = new Map<string, Omit<TaskLinkedEvent, 'detail' | 'taskCompletionRate'>>();

  allProjects.forEach((project) => {
    const eventUuid = String(project?.eventUuid || '').trim();
    if (!eventUuid) return;
    const current = aggregateMap.get(eventUuid) || {
      eventUuid,
      eventName: String(project?.eventName || 'イベント名未設定'),
      eventDate: (project?.eventDate as string | null) || null,
      projectCount: 0,
      totalTaskCount: 0,
      openTaskCount: 0,
      doneTaskCount: 0,
      projectNames: [],
    };
    current.projectCount += 1;
    current.totalTaskCount += Number(project?.totalTaskCount || 0);
    current.openTaskCount += Number(project?.openTaskCount || 0);
    current.doneTaskCount += Number(project?.doneTaskCount || 0);
    if (project?.name) current.projectNames.push(String(project.name));
    if (!current.eventDate && project?.eventDate) current.eventDate = String(project.eventDate);
    if (!current.eventName && project?.eventName) current.eventName = String(project.eventName);
    aggregateMap.set(eventUuid, current);
  });

  const eventUuids = Array.from(aggregateMap.keys());
  const detailPairs = await Promise.all(
    eventUuids.map(async (eventUuid) => [eventUuid, await getEventDetail(eventUuid)] as const)
  );
  const detailMap = new Map(detailPairs);

  return eventUuids
    .map((eventUuid) => {
      const aggregate = aggregateMap.get(eventUuid);
      const detail = detailMap.get(eventUuid);
      const eventDate = String(detail?.event_date || aggregate?.eventDate || '') || null;
      const eventName = String(detail?.name || aggregate?.eventName || 'イベント名未設定');
      const totalTaskCount = Number(aggregate?.totalTaskCount || 0);
      const doneTaskCount = Number(aggregate?.doneTaskCount || 0);
      const taskCompletionRate =
        totalTaskCount > 0 ? Math.round((doneTaskCount / totalTaskCount) * 100) : 0;
      return {
        ...aggregate,
        eventUuid,
        eventName,
        eventDate,
        totalTaskCount,
        doneTaskCount,
        detail,
        taskCompletionRate: Math.max(0, Math.min(100, taskCompletionRate)),
      } as TaskLinkedEvent;
    })
    .sort((a, b) => {
      const aDate = a.eventDate ? new Date(a.eventDate).getTime() : Number.POSITIVE_INFINITY;
      const bDate = b.eventDate ? new Date(b.eventDate).getTime() : Number.POSITIVE_INFINITY;
      if (aDate !== bDate) return aDate - bDate;
      return String(a.eventName || '').localeCompare(String(b.eventName || ''), 'ja');
    });
}

export function useTaskLinkedEventsQuery() {
  return useQuery({
    queryKey: ['task-linked-events'],
    queryFn: listTaskLinkedEvents,
  });
}
